import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useProductSettings } from '@workspace/utils-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Search, X, AlertCircle, MessageSquareMore } from 'lucide-react';
import type { Quarter } from '../../lib/fiscal-year';
import { useDivisions, useSoftwareCompanies, useProducts, useMultipleQuartersProjectEvaluations, useQuartersData, useMultipleProductsQuartersData, useEpicCounts } from '../../hooks';
import type { Product, SoftwareCompany } from '../../api/shared/types';
import { hasDataForAnyQuarter } from '../../api/projects';
import { getAllQuarters, getDefaultFiscalYear, getSelectableFiscalYears, getFiscalYearQuarterString, formatFiscalYear, getVisibleQuarters } from '../../lib/fiscal-year';
import { checkBatchAdminAccess } from '../../api/batch-admin';
import type { Settings } from '../../AppContainer';
import { toast } from 'sonner';
import { QuarterCard, ProductsCard } from '../../components/features/quarter-card';
import { ProjectSummaryCard } from '../../components/features/project';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { ApiErrorPage, extractServiceError } from '../../components/common';
import { FeedbackModal } from '../../components/features/help';

export function LandingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { apiUrl } = useProductSettings<Settings>();
  const [isAdministrator, setIsAdministrator] = useState(false);

  useEffect(() => {
    let active = true;
    checkBatchAdminAccess(apiUrl)
      .then((result) => {
        if (active) setIsAdministrator(result.permission === 'Administrator');
      })
      .catch(() => {
        if (active) setIsAdministrator(false);
      });
    return () => {
      active = false;
    };
  }, [apiUrl]);

  // Use TanStack Query hooks for data fetching
  const { data: divisions = [], isLoading: loadingDivisions, isError: divisionsError, error: divisionsErrorObj } = useDivisions();
  const {
    data: softwareCompanies = [],
    isLoading: loadingSoftwareCompanies,
    isError: softwareCompaniesError,
    error: softwareCompaniesErrorObj,
  } = useSoftwareCompanies();

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedDivision, setSelectedDivision] = useState<string>('all');
  const [selectedSWC, setSelectedSWC] = useState<string>('all');
  const [selectedQuarter, setSelectedQuarter] = useState<Quarter | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<
    'pending' | 'approved' | 'reopened' | 'all'
  >('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number>(getDefaultFiscalYear());

  const [filterSoftwareCompanies, setFilterSoftwareCompanies] = useState<SoftwareCompany[]>([]);

  // Fetch all products once — filtering is done client-side to avoid
  // re-fetching (and skeleton flashing) on every Division/SWC change.
  const { data: products = [], isLoading: loadingProducts, isError: productsError, error: productsErrorObj } = useProducts({ limit: 1000, fiscalYear: formatFiscalYear(selectedFiscalYear) });

  const hasDataError = divisionsError || softwareCompaniesError || productsError;

  const uniqueProduct = useMemo(() => {
    if (!products?.length) return [];
    const seen = new Set<string>();
    const uniqueItems: Product[] = [];
    for (const item of products) {
      if (!item.project_name) continue;
      if (seen.has(item.project_name)) continue;
      seen.add(item.project_name);
      uniqueItems.push(item);
    }
    return uniqueItems;
  }, [products]);

  // Derive selected names for client-side filtering
  const selectedDivisionName = useMemo(() => {
    if (selectedDivision === 'all') return null;
    if (selectedDivision === 'unassigned') return '__UNASSIGNED__';
    return divisions.find((d) => d.id === selectedDivision)?.name ?? null;
  }, [selectedDivision, divisions]);

  const selectedSWCName = useMemo(() => {
    if (selectedSWC === 'all') return null;
    if (selectedSWC === 'unassigned') return '__UNASSIGNED__';
    return softwareCompanies.find((c) => c.id === selectedSWC)?.name ?? null;
  }, [selectedSWC, softwareCompanies]);

  const quarters = useMemo(
    () => isAdministrator
      ? getVisibleQuarters(selectedFiscalYear, true)
      : getAllQuarters(selectedFiscalYear),
    [selectedFiscalYear, isAdministrator],
  );

  // Build available quarters in the canonical format ("FY26 Q1") used by every endpoint
  const availableQuarters = useMemo(
    () => quarters.filter((q) => q.isAvailable)
      .map((q) => getFiscalYearQuarterString(q.fiscalYear, q.quarter)),
    [quarters],
  );

  // Pre-fetch epic counts once on page load — runs in parallel with products, divisions, etc.
  // Data is cached for the entire session (staleTime: Infinity) so product changes don't re-fetch.
  const { isLoading: epicCountsLoading } = useEpicCounts(availableQuarters);

  // Fetch quarters data (epic counts + review statuses) from backend APIs
  const selectedProjectName = selectedProduct ? selectedProduct.project_name : '';
  const { data: quartersData = {}, isLoading: quartersDataLoading } = useQuartersData(selectedProjectName, availableQuarters, formatFiscalYear(selectedFiscalYear));

  // Fetch project evaluations for all available quarters
  const { data: projectEvaluationsLookup = {} } = useMultipleQuartersProjectEvaluations(availableQuarters);

  const updateUrlParams = useCallback(
    (division: string, swc: string, product: string | null, quarter: string, status: string, search: string, fy: number) => {
      const params = new URLSearchParams();
      const defaultFy = getDefaultFiscalYear();
      if (fy !== defaultFy) {
        params.set('FY', fy.toString());
      }
      if (division !== 'all') {
        params.set('division', division);
      }
      if (swc !== 'all') {
        params.set('SoftCo', swc);
      }
      if (product) {
        params.set('Product', product);
      }
      if (quarter !== 'all') {
        params.set('Quarter', quarter);
      }
      if (status !== 'all') {
        params.set('Status', status);
      }
      if (search.trim()) {
        params.set('Search', search.trim());
      }
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  // Initialize selections from URL params when data is loaded
  /* eslint-disable react-hooks/set-state-in-effect -- intentional sync of URL params to state on mount */
  useEffect(() => {
    if (loadingDivisions || loadingSoftwareCompanies || loadingProducts || initialLoadComplete) {
      return;
    }

    const fyParam = searchParams.get('FY');
    const divisionParam = searchParams.get('division');
    const swcParam = searchParams.get('SoftCo');
    const productParam = searchParams.get('Product');
    const quarterParam = searchParams.get('Quarter');
    const statusParam = searchParams.get('Status');
    const searchParam = searchParams.get('Search');

    let initialFY = getDefaultFiscalYear();
    let initialDivision = 'all';
    let initialSWC = 'all';
    let initialProduct: Product | null = null;
    let initialQuarter: Quarter | 'all' = 'all';
    let initialStatus: 'pending' | 'approved' | 'reopened' | 'all' = 'all';
    let initialSearch = '';

    if (fyParam) {
      const parsed = Number.parseInt(fyParam, 10);
      if (!Number.isNaN(parsed)) {
        initialFY = parsed;
      }
    }

    if (divisionParam === 'unassigned') {
      initialDivision = 'unassigned';
    } else if (divisionParam && divisions.some((d) => d.id === divisionParam)) {
      initialDivision = divisionParam;
    }

    if (swcParam === 'unassigned') {
      initialSWC = 'unassigned';
    } else if (swcParam && softwareCompanies.some((c) => c.id === swcParam)) {
      const company = softwareCompanies.find((c) => c.id === swcParam);
      if (company && (initialDivision === 'all' || company.division_id === initialDivision)) {
        initialSWC = swcParam;
        if (initialDivision === 'all') {
          initialDivision = company.division_id;
        }
      }
    }

    if (productParam) {
      const product = uniqueProduct.find((p) => p.project_name === productParam);
      if (product) {
        const divisionForProduct = divisions.find((d) => d.name === product.division);
        const swcForProduct = softwareCompanies.find((c) => c.name === product.software_company);
        const matchesDivision = initialDivision === 'all' || divisionForProduct?.id === initialDivision;
        const matchesSWC = initialSWC === 'all' || swcForProduct?.id === initialSWC;
        if (matchesDivision && matchesSWC) {
          initialProduct = product;
          if (initialDivision === 'all' && divisionForProduct) {
            initialDivision = divisionForProduct.id;
          }
          if (initialSWC === 'all' && swcForProduct) {
            initialSWC = swcForProduct.id;
          }
        }
      }
    }

    if (quarterParam && ['Q1', 'Q2', 'Q3', 'Q4'].includes(quarterParam)) {
      initialQuarter = quarterParam as Quarter;
    }

    if (statusParam && ['pending', 'approved', 'reopened'].includes(statusParam)) {
      initialStatus = statusParam as typeof initialStatus;
    }

    if (searchParam) {
      initialSearch = searchParam;
    }

    setSelectedFiscalYear(initialFY);
    setSelectedDivision(initialDivision);
    setSelectedSWC(initialSWC);
    setSelectedProduct(initialProduct);
    setSelectedQuarter(initialQuarter);
    setSelectedStatus(initialStatus);
    setSearchQuery(initialSearch);
    setInitialLoadComplete(true);
  }, [
    loadingDivisions,
    loadingSoftwareCompanies,
    loadingProducts,
    divisions,
    softwareCompanies,
    products,
    searchParams,
    initialLoadComplete,
    uniqueProduct,
  ]);

  useEffect(() => {
    if (initialLoadComplete) {
      updateUrlParams(selectedDivision, selectedSWC, selectedProduct?.project_name ?? null, selectedQuarter, selectedStatus, searchQuery, selectedFiscalYear);
    }
  }, [selectedDivision, selectedSWC, selectedProduct, selectedQuarter, selectedStatus, searchQuery, selectedFiscalYear, initialLoadComplete, updateUrlParams]);

  useEffect(() => {
    if (selectedDivision === 'all') {
      setFilterSoftwareCompanies(softwareCompanies);
      return;
    }
    if (selectedDivision === 'unassigned') {
      setFilterSoftwareCompanies([]);
      return;
    }
    const filtered = softwareCompanies.filter((c) => c.division_id.startsWith(selectedDivision));
    return filtered.length > 0 ? setFilterSoftwareCompanies(filtered) : setFilterSoftwareCompanies(softwareCompanies);
  }, [selectedDivision]);

  //   const filteredSoftwareCompanies = useMemo(() => {
  //     if (selectedDivision === 'all') {
  //       return softwareCompanies;
  //     }
  //     const filtered = softwareCompanies.filter((c) => c.division_id === selectedDivision);
  //     return filtered.length > 0 ? filtered : softwareCompanies;
  //   }, [softwareCompanies, selectedDivision]);

  const filteredProducts = useMemo(() => {
    let filtered = uniqueProduct;

    if (selectedDivisionName) {
      if (selectedDivisionName === '__UNASSIGNED__') {
        filtered = filtered.filter((p) => !p.division);
      } else {
        filtered = filtered.filter((p) => p.division === selectedDivisionName);
      }
    }

    if (selectedSWCName) {
      if (selectedSWCName === '__UNASSIGNED__') {
        filtered = filtered.filter((p) => !p.software_company);
      } else {
        filtered = filtered.filter((p) => p.software_company === selectedSWCName);
      }
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((product) => product.project_name.toLowerCase().includes(query));
    }

    return [...filtered].sort((a, b) => a.project_name.localeCompare(b.project_name));
  }, [uniqueProduct, selectedDivisionName, selectedSWCName, searchQuery]);

  // Derive filteredProductCodes from filteredProducts
  // to avoid duplicating the filter logic.
  const filteredProductCodes = useMemo(
    () => filteredProducts.filter((p) => p.project_name != null).map((p) => p.project_name),
    [filteredProducts],
  );

  // Fetch multiple-products quarters data (epic counts + review statuses) from backend APIs
  const multiProjectNames = !selectedProduct ? filteredProductCodes : [];
  const { data: multipleProductsQuartersData, isLoading: multiQuartersLoading } = useMultipleProductsQuartersData(
    multiProjectNames,
    availableQuarters,
    formatFiscalYear(selectedFiscalYear),
  );

  const suppressedFilteredProducts = useMemo(() => {
    if (epicCountsLoading || quartersDataLoading || multiQuartersLoading) {
      return filteredProducts;
    }
    return filteredProducts.filter((product) => {
      if (!product.project_name) return true;
      const productQuarters = multipleProductsQuartersData?.[product.project_name] ?? {};
      return hasDataForAnyQuarter(productQuarters, availableQuarters);
    });
  }, [filteredProducts, multipleProductsQuartersData, epicCountsLoading, quartersDataLoading, multiQuartersLoading, availableQuarters]);

  const handleDivisionChange = (divisionId: string) => {
    setSelectedDivision(divisionId);

    if (divisionId === 'unassigned') {
      setSelectedSWC('all');
    } else if (divisionId !== 'all') {
      const currentSWCValid = softwareCompanies.some((c) => c.id === selectedSWC && c.division_id === divisionId);
      if (!currentSWCValid) {
        setSelectedSWC('all');
      }
    }

    // Always clear product selection when division changes - user should select from product cards
    setSelectedProduct(null);
  };

  const handleSWCChange = (swcId: string) => {
    setSelectedSWC(swcId);

    // Always clear product selection when SWC changes - user should select from product cards
    setSelectedProduct(null);
  };

  const handleProductChange = (projectName: string) => {
    const product = uniqueProduct.find((p) => p.project_name === projectName);
    if (product) {
      setSelectedProduct(product);
    }
  };

  const handleFiscalYearChange = (fy: string) => {
    setSelectedFiscalYear(Number(fy));
    setSelectedProduct(null);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedFiscalYear(getDefaultFiscalYear());
    setSelectedDivision('all');
    setSelectedSWC('all');
    setSelectedQuarter('all');
    setSelectedStatus('all');
    setSelectedProduct(null);
  };

  const handleQuarterChange = (quarter: string) => {
    setSelectedQuarter(quarter as Quarter | 'all');
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status as 'pending' | 'approved' | 'reopened' | 'all');
  };

  const handleCardClick = async (projectName: string, fiscalYearQuarter: string) => {
    try {
      navigate(
        `/review/${encodeURIComponent(projectName)}/${encodeURIComponent(fiscalYearQuarter)}`,
      );
    } catch (error) {
      console.error('ProjectApproval_ Error during navigation:', error);
      toast.error('Failed to load project. Please try again.');
    }
  };

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    selectedFiscalYear !== getDefaultFiscalYear() ||
    selectedDivision !== 'all' ||
    selectedSWC !== 'all' ||
    selectedQuarter !== 'all' ||
    selectedStatus !== 'all' ||
    selectedProduct !== null;

  const getDisplayStatus = useCallback(
    (
      quarterData: { status?: string; hasAssessedEpics?: boolean } | undefined,
    ): 'pending' | 'approved' | 'reopened' => {
      if (!quarterData?.status) return 'pending';
      switch (quarterData.status) {
        case 'approved':
        case 'archived':
          return 'approved';
        case 'reopened':
        case 'withdrawn':
          return 'reopened';
        case 'draft':
        case 'inprogress':
        case 'pendingapproval':
        case 'rejected':
        default:
          return 'pending';
      }
    },
    [],
  );

  const filteredQuarters = useMemo(() => {
    let filtered = quarters;
    if (selectedQuarter !== 'all') {
      filtered = filtered.filter((q) => q.quarter === selectedQuarter);
    }
    return filtered;
  }, [quarters, selectedQuarter]);

  const statusFilteredQuarters = useMemo(() => {
    if (selectedStatus === 'all' || !selectedProduct) {
      return filteredQuarters;
    }
    return filteredQuarters.filter((q) => {
      const fiscalYearQuarterKey = getFiscalYearQuarterString(q.fiscalYear, q.quarter);
      const quarterData = quartersData[fiscalYearQuarterKey];
      const displayStatus = getDisplayStatus(quarterData);
      return displayStatus === selectedStatus;
    });
  }, [filteredQuarters, selectedStatus, selectedProduct, quartersData, getDisplayStatus]);

  // Filter products by status when a quarter is selected but no product is selected
  const statusFilteredProducts = useMemo(() => {
    if (selectedStatus === 'all') {
      return filteredProducts;
    }
    if (selectedQuarter !== 'all') {
      const quarterInfo = filteredQuarters.find((q) => q.quarter === selectedQuarter);
      if (!quarterInfo) return filteredProducts;
      const fiscalYearQuarterKey = getFiscalYearQuarterString(quarterInfo.fiscalYear, quarterInfo.quarter);
      return filteredProducts.filter((product) => {
        if (product.project_name == null) return false;
        const productQuartersData = multipleProductsQuartersData[product.project_name] || {};
        const quarterData = productQuartersData[fiscalYearQuarterKey];
        const displayStatus = getDisplayStatus(quarterData);
        return displayStatus === selectedStatus;
      });
    }
    return filteredProducts.filter((product) => {
      if (product.project_name == null) return false;
      const productQuartersData = multipleProductsQuartersData[product.project_name] || {};
      return quarters
        .filter((q) => q.isAvailable)
        .some((q) => {
          const key = getFiscalYearQuarterString(q.fiscalYear, q.quarter);
          const quarterData = productQuartersData[key];
          const displayStatus = getDisplayStatus(quarterData);
          return displayStatus === selectedStatus;
        });
    });
  }, [
    filteredProducts,
    selectedStatus,
    selectedQuarter,
    filteredQuarters,
    multipleProductsQuartersData,
    getDisplayStatus,
    quarters,
  ]);

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="container mx-auto px-4 py-8 space-y-8">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFeedbackModal(true)}
            className="gap-2"
          >
            <MessageSquareMore className="h-4 w-4" />
            Feedback
          </Button>
        </div>
        <div className="flex flex-wrap items-end gap-9">
          <div className="flex flex-col gap-2 w-[120px]">
            <label htmlFor="fy-filter" className="text-sm font-medium">
              Fiscal Year
            </label>
            <Select value={selectedFiscalYear.toString()} onValueChange={handleFiscalYearChange}>
              <SelectTrigger id="fy-filter" className="w-full">
                <SelectValue placeholder="Select FY" />
              </SelectTrigger>
              <SelectContent>
                {getSelectableFiscalYears().map((fy) => (
                  <SelectItem key={fy} value={fy.toString()}>
                    {formatFiscalYear(fy)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2 w-[160px]">
            <label htmlFor="division-filter" className="text-sm font-medium">
              Division
            </label>
            {loadingDivisions ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <div className="flex items-center gap-1 w-full">
                <Select value={selectedDivision} onValueChange={handleDivisionChange}>
                  <SelectTrigger id="division-filter" className="w-full">
                    <SelectValue placeholder="All Divisions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Divisions</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {divisions.map((division) => (
                      <SelectItem key={division.id} value={division.id}>
                        {division.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedDivision !== 'all' && (
                  <button
                    onClick={() => handleDivisionChange('all')}
                    className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors rounded hover:bg-gray-100"
                    aria-label="Clear division"
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 w-[200px]">
            <label htmlFor="swc-filter" className="text-sm font-medium">
              Software Company
            </label>
            {loadingSoftwareCompanies ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <div className="flex items-center gap-1 w-full">
                <Select
                  value={selectedSWC}
                  onValueChange={handleSWCChange}
                  disabled={(selectedDivision === 'all' || filterSoftwareCompanies.length === 0)
                    && selectedDivision !== 'unassigned'}
                >
                  <SelectTrigger id="swc-filter" className="w-full">
                    <SelectValue placeholder="All Software Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Software Companies</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {filterSoftwareCompanies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedSWC !== 'all' && (
                  <button
                    onClick={() => handleSWCChange('all')}
                    className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors rounded hover:bg-gray-100"
                    aria-label="Clear software company"
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 w-[200px]">
            <label htmlFor="product-filter" className="text-sm font-medium">
              Project {suppressedFilteredProducts.length > 0 && `(${suppressedFilteredProducts.length})`}
            </label>
            {loadingProducts ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <div className="flex items-center gap-1 w-full">
                <Select
                  key={selectedProduct?.project_name ?? 'no-product'}
                  value={selectedProduct?.project_name?.toString()}
                  onValueChange={handleProductChange}
                  disabled={(selectedDivision === 'all' || suppressedFilteredProducts.length === 0)
                    && selectedDivision !== 'unassigned'}
                >
                  <SelectTrigger id="product-filter" className="w-full">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppressedFilteredProducts
                      .filter((product) => product.project_name != null)
                      .map((product) => (
                        <SelectItem key={product.project_name} value={product.project_name.toString()}>
                          {product.project_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {selectedProduct && (
                  <button
                    onClick={() => setSelectedProduct(null)}
                    className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors rounded hover:bg-gray-100"
                    aria-label="Clear project"
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 w-[140px]">
            <label htmlFor="quarter-filter" className="text-sm font-medium">
              Quarter
            </label>
            <div className="flex items-center gap-1 w-full">
              <Select value={selectedQuarter} onValueChange={handleQuarterChange}>
                <SelectTrigger id="quarter-filter" className="w-full">
                  <SelectValue placeholder="All Quarters" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Quarters</SelectItem>
                  {quarters.map((q) => (
                    <SelectItem key={q.quarter} value={q.quarter}>
                      {q.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedQuarter !== 'all' && (
                <button
                  onClick={() => handleQuarterChange('all')}
                  className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors rounded hover:bg-gray-100"
                  aria-label="Clear quarter"
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 w-[150px]">
            <label htmlFor="status-filter" className="text-sm font-medium">
              Status
            </label>
            <div className="flex items-center gap-1 w-full">
              <Select value={selectedStatus} onValueChange={handleStatusChange}>
                <SelectTrigger id="status-filter" className="w-full">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="reopened">Re-opened</SelectItem>
                </SelectContent>
              </Select>
              {selectedStatus !== 'all' && (
                <button
                  onClick={() => handleStatusChange('all')}
                  className="flex-shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors rounded hover:bg-gray-100"
                  aria-label="Clear status"
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 w-[200px]">
            <label htmlFor="search-filter" className="text-sm font-medium">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search-filter"
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 w-full"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Clear search"
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {hasActiveFilters && (
            <div className="flex flex-col gap-2">
              <div className="h-[20px]" />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={handleClearFilters} aria-label="Clear all filters">
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Clear all filters</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </div>

        {hasDataError ? (
          <ApiErrorPage
            title="Unable to load dashboard data"
            errors={[
              { label: 'divisions', hasError: divisionsError },
              { label: 'software companies', hasError: softwareCompaniesError },
              { label: 'projects', hasError: productsError },
            ]}
            serviceError={extractServiceError(productsErrorObj) ?? extractServiceError(divisionsErrorObj) ?? extractServiceError(softwareCompaniesErrorObj)}
            onRetry={() => queryClient.invalidateQueries()}
          />
        ) : loadingDivisions || loadingSoftwareCompanies || loadingProducts ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        ) : selectedProduct ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {statusFilteredQuarters.map((quarterInfo) => {
                const fyqKey = getFiscalYearQuarterString(quarterInfo.fiscalYear, quarterInfo.quarter);
                const evaluation = projectEvaluationsLookup[selectedProduct.project_name]?.[fyqKey] ?? null;
                return (
                  <QuarterCard
                    key={quarterInfo.quarter}
                    quarterInfo={quarterInfo}
                    selectedProduct={selectedProduct}
                    quartersData={quartersData}
                    onCardClick={handleCardClick}
                    projectEvaluation={evaluation}
                    epicCountsLoading={epicCountsLoading}
                    statusLoading={quartersDataLoading}
                  />
                );
              })}
            </div>
          </div>
        ) : suppressedFilteredProducts.length === 0 && !loadingProducts ? (
          <Alert variant="info">
            <AlertCircle className="h-4 w-4" />
            <div>
              <AlertTitle>No projects found</AlertTitle>
              <AlertDescription>
                No projects match the current filters. Try adjusting your filters or clearing them.
              </AlertDescription>
            </div>
          </Alert>
        ) : selectedQuarter === 'all' && selectedStatus === 'all' ? (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">All Projects ({suppressedFilteredProducts.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suppressedFilteredProducts
                .filter((product) => product.project_name != null)
                .map((product) => {
                  const productQuartersData = multipleProductsQuartersData[product.project_name] || {};
                  return (
                    <ProjectSummaryCard
                      key={product.project_name}
                      product={product}
                      quartersData={productQuartersData}
                      quarters={quarters}
                      statusLoading={multiQuartersLoading}
                      onClick={() => {
                        const divMatch = divisions.find((d) => d.name === product.division);
                        const swcMatch = softwareCompanies.find((c) => c.name === product.software_company);
                        if (divMatch) setSelectedDivision(divMatch.id);
                        else setSelectedDivision('all');
                        if (swcMatch) setSelectedSWC(swcMatch.id);
                        else setSelectedSWC('all');
                        setSelectedProduct(product);
                      }}
                    />
                  );
                })}
            </div>
          </div>
        ) : selectedQuarter === 'all' && selectedStatus !== 'all' ? (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Projects ({statusFilteredProducts.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {statusFilteredProducts
                .filter((product) => product.project_name != null)
                .map((product) => {
                  const productQuartersData = multipleProductsQuartersData[product.project_name] || {};
                  const matchingQuarters = quarters.filter((q) => {
                    if (!q.isAvailable) return false;
                    const key = getFiscalYearQuarterString(q.fiscalYear, q.quarter);
                    const quarterData = productQuartersData[key];
                    const displayStatus = getDisplayStatus(quarterData);
                    return displayStatus === selectedStatus;
                  });
                  return matchingQuarters.map((quarterInfo) => {
                    const fyqKey = getFiscalYearQuarterString(quarterInfo.fiscalYear, quarterInfo.quarter);
                    const evaluation = projectEvaluationsLookup[product.project_name]?.[fyqKey] ?? null;
                    return (
                        <ProductsCard
                          key={`${product.project_name}-${quarterInfo.quarter}`}
                          quarterInfo={quarterInfo}
                          product={product}
                          quartersData={productQuartersData}
                          onCardClick={handleCardClick}
                          onProductSelect={(p) => {
                            const divMatch = divisions.find((d) => d.name === p.division);
                            const swcMatch = softwareCompanies.find((c) => c.name === p.software_company);
                            if (divMatch) {
                              setSelectedDivision(divMatch.id);
                            } else {
                              setSelectedDivision('all');
                            }
                            if (swcMatch) {
                              setSelectedSWC(swcMatch.id);
                            } else {
                              setSelectedSWC('all');
                            }
                            setSelectedProduct(p);
                          }}
                          projectEvaluation={evaluation}
                          epicCountsLoading={epicCountsLoading}
                    statusLoading={quartersDataLoading}
                        />
                    );
                  });
                })}
            </div>
          </div>
        ) : selectedQuarter !== 'all' ? (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Projects ({statusFilteredProducts.length})</h2>
            <p className="text-sm text-muted-foreground -mt-4">
              {filteredQuarters.find((q) => q.quarter === selectedQuarter)?.label || selectedQuarter}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {statusFilteredProducts
                .filter((product) => product.project_name != null)
                .map((product) => {
                  const quarterInfo = filteredQuarters.find((q) => q.quarter === selectedQuarter);
                  const productQuartersData = multipleProductsQuartersData[product.project_name] || {};
                  if (!quarterInfo) return null;
                  const fyqKey = getFiscalYearQuarterString(quarterInfo.fiscalYear, quarterInfo.quarter);
                  const evaluation = projectEvaluationsLookup[product.project_name]?.[fyqKey] ?? null;
                  return (
                    <ProductsCard
                      key={product.project_name}
                      quarterInfo={quarterInfo}
                      product={product}
                      quartersData={productQuartersData}
                      onCardClick={handleCardClick}
                      onProductSelect={(p) => {
                        const divMatch = divisions.find((d) => d.name === p.division);
                        const swcMatch = softwareCompanies.find((c) => c.name === p.software_company);
                        if (divMatch) {
                          setSelectedDivision(divMatch.id);
                        } else {
                          setSelectedDivision('all');
                        }
                        if (swcMatch) {
                          setSelectedSWC(swcMatch.id);
                        } else {
                          setSelectedSWC('all');
                        }
                        setSelectedProduct(p);
                      }}
                      projectEvaluation={evaluation}
                      epicCountsLoading={epicCountsLoading}
                      statusLoading={quartersDataLoading}
                    />
                  );
                })}
            </div>
          </div>
        ) : null}
        <FeedbackModal open={showFeedbackModal} onOpenChange={setShowFeedbackModal} />
      </div>
    </div>
  );
}
